import type { JSX } from 'react';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { Store, useStore } from 'dg-web-shared/lib/Flux.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { Response } from 'dg-web-shared/lib/HttpResponse.ts';
import { isDefined, thenElse } from 'dg-web-shared/lib/MaybeV2.ts';
import { ElementNamer } from 'dg-web-shared/lib/ReactHelpers.tsx';
import * as Http from '../../api/Http.ts';
import * as AsyncRequest from '../../AsyncRequest.ts';
import { Translation } from '../../common/i18n/Text.ts';
import * as OperatorLoginsState from '../../common/state/OperatorLoginsState.ts';
import {
    getLoginById,
    getLoginTitle,
} from '../../common/state/OperatorLoginsState.ts';
import { LabeledText } from '../../ui/labeled-elements/LabeledText.tsx';
import {
    ButtonDropdown,
    ButtonDropdownItem,
} from '../../ui/slidein/ButtonDropdown.tsx';
import {
    ConfirmationHeader,
    ErrorHeader,
    HalfSlideIn,
    LoaderHeader,
    SlideInBody,
    SlideInHeaderButtonsContainer,
    SlideInHeaderTexts,
    StandardFirstLevelHeader,
} from '../../ui/slidein/Slidein.tsx';
import * as CustomerClearanceTexts from '../i18n/CustomerClearanceTexts.ts';
import * as CustomerClearanceState from '../state/CustomerClearanceState.ts';

const cn = ElementNamer('CustomerClearanceDetailSlideIn');

const deleteClearanceByContractExternalId = AsyncRequest.request(
    Http.OperatorAccount.CustomerClearance.delByContractId,
    (store: Store, res: Response): string => {
        CustomerClearanceState.DeleteResponse.setResponse(store, res);
        if (
            CustomerClearanceState.DeleteResponse.get(store).statusCode.cls
                .success
        ) {
            CustomerClearanceState.List.reset(store);
            CustomerClearanceState.Detail.reset(store);
        }
        return cn('deleteClearanceByContractId');
    },
);

const closeDetail = (store: Store): string => {
    CustomerClearanceState.DeleteResponse.reset(store);
    CustomerClearanceState.Detail.reset(store);
    return cn('closeDetail');
};

export interface CustomerClearanceDetailSlideInTexts {
    slideInHeader: Translation;
    confirmDelete: Translation;
    deletingClearance: Translation;
    deleteError: Translation;
    editLabel: Translation;
    delete: Translation;
}

export function CustomerClearanceDetailSlideIn() {
    const { storeState, update } = useStore(store => ({
        detail: CustomerClearanceState.Detail.get(store),
        operatorLogins: OperatorLoginsState.List.get(store),
        deleteResponse: CustomerClearanceState.DeleteResponse.get(store),
    }));
    const { language } = useLanguage();

    const texts =
        CustomerClearanceTexts.customerClearanceDetailSlideInTexts[language];
    const tableTexts =
        CustomerClearanceTexts.customerClearanceTableTexts[language];

    function renderBody(): JSX.Element | null {
        const clearance = storeState.detail.selectedClearance;

        if (!clearance) {
            return null;
        }

        return (
            <SlideInBody>
                <LabeledText label={tableTexts.createdDate()}>
                    {Formatter.dayMonthYear(clearance.created)}
                </LabeledText>
                <LabeledText label={tableTexts.createdBy()}>
                    {thenElse(
                        getLoginById(
                            storeState.operatorLogins.data,
                            clearance.adminId,
                        ),
                        l => getLoginTitle(l),
                        '',
                    )}
                </LabeledText>
                <LabeledText label={tableTexts.customerNr()}>
                    {clearance.customerNr}
                </LabeledText>
                <LabeledText label={tableTexts.remark()}>
                    {CustomerClearanceState.remark(clearance)}
                </LabeledText>
                <LabeledText label={tableTexts.permitTypeName()}>
                    {clearance.permitTypeName}
                </LabeledText>
            </SlideInBody>
        );
    }

    function renderHeader(): JSX.Element {
        if (storeState.deleteResponse.pending) {
            return <LoaderHeader title={texts.deletingClearance()} />;
        } else if (storeState.deleteResponse.statusCode.cls.error) {
            return (
                <ErrorHeader
                    language={language}
                    title={texts.deleteError()}
                    onCancel={() => update(closeDetail)}
                />
            );
        } else if (storeState.detail.confirmDeleteVisible) {
            return (
                <ConfirmationHeader
                    language={language}
                    title={texts.confirmDelete()}
                    onCancel={() =>
                        update(store =>
                            CustomerClearanceState.Detail.stateWrite(store, {
                                confirmDeleteVisible: false,
                            }),
                        )
                    }
                    onConfirm={() => {
                        if (storeState.detail.selectedClearance) {
                            update(store =>
                                deleteClearanceByContractExternalId(
                                    store,
                                    storeState.detail.selectedClearance
                                        ?.contractExternalId ?? undefined,
                                ),
                            );
                        }
                    }}
                />
            );
        } else {
            return (
                <StandardFirstLevelHeader
                    onClose={() => update(CustomerClearanceState.Detail.reset)}
                >
                    <SlideInHeaderTexts
                        hasLeftIcon={false}
                        title={texts.slideInHeader()}
                    />
                    <SlideInHeaderButtonsContainer>
                        <ButtonDropdown label={texts.editLabel()}>
                            <ButtonDropdownItem
                                label={texts.delete()}
                                onClick={() =>
                                    update(store =>
                                        CustomerClearanceState.Detail.stateWrite(
                                            store,
                                            {
                                                confirmDeleteVisible: true,
                                            },
                                        ),
                                    )
                                }
                            />
                        </ButtonDropdown>
                    </SlideInHeaderButtonsContainer>
                </StandardFirstLevelHeader>
            );
        }
    }

    return (
        <HalfSlideIn open={isDefined(storeState.detail.selectedClearance)}>
            {renderBody()}
            {renderHeader()}
        </HalfSlideIn>
    );
}
